<script setup>
import {ref, watch, computed, onMounted} from 'vue';
import $http from "@/axios.config";
import {useI18n} from "vue-i18n";
import {showToast} from "@/utils/swiperToast";
import FormSelect from "@/components/ui/form/FormSelect.vue";
import helpers from "@root/helpers.js";

const {t, locale} = useI18n();

const props = defineProps({
  orderId: {
    type: String,
    required: true
  },
  badge: {
    type: Object,
    required: true
  }
});

const showModal = ref(false);
const isSubmitting = ref(false);
const allBadges = ref({});
const errors = ref({});
const selectedBadgeId = ref(props.badge._id);

const badgeName = computed(() => helpers.getBadgeName(props.badge, locale.value));

const badgesOptions = computed(() => {
  return Object.entries(allBadges.value).map(([value, label]) => ({
    value,
    label
  }));
});

async function getBadges() {
  try {
    const res = await $http.get('/api/badges');
    allBadges.value = res.data.reduce((acc, badge) => {
      acc[badge._id] = helpers.getBadgeName(badge, locale.value);
      return acc;
    }, {});
  } catch (err) {
    console.error('BadgeUpdater:getBadges', {error: err});
  }
}

function openModal() {
  selectedBadgeId.value = props.badge._id;
  errors.value = {};
  showModal.value = true;
}

function handleCancel() {
  showModal.value = false;
  selectedBadgeId.value = props.badge._id;
  errors.value = {};
}

async function updateOrderBadge() {
  errors.value = {};

  if (!selectedBadgeId.value) {
    errors.value.badgeId = t('validation.required', {field: t('labels.badge')});
    return;
  }

  isSubmitting.value = true;

  const data = {
    badgeId: selectedBadgeId.value,
  };

  try {
    await $http.put('/api/orders/' + props.orderId, data);
    await showToast(t('messages.orderUpdated'), 'success')
    showModal.value = false;
  } catch (err) {
    await showToast(t('alerts.error'), 'error')
  } finally {
    isSubmitting.value = false;
  }
}

watch(() => props.badge._id, (newId) => {
  selectedBadgeId.value = newId;
});

onMounted(() => {
  getBadges();
});
</script>

<template>
  <div class="btn-group" role="group">
    <div
        class="badge rounded-1 rounded-end-0 d-flex align-items-center"
        :class="[props.badge.type ? `bg-${props.badge.type}` : '']"
    >
      {{ badgeName }}
    </div>

    <button
        type="button"
        class="btn btn-primary btn-sm d-flex align-items-center justify-content-center"
        @click.stop="openModal"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
           fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16">
        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
        <path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
      </svg>
    </button>
  </div>

  <teleport to="body">
    <div v-if="showModal" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <form @submit.prevent="updateOrderBadge" class="row g-3">

              <div class="col-12">
                <FormSelect
                    id="badgeSelect"
                    :label="t('labels.badge')"
                    v-model="selectedBadgeId"
                    :options="badgesOptions"
                    :class="{ 'is-invalid': errors.badgeId }"
                />
              </div>

              <div class="col-6">
                <button type="button" class="btn btn-danger w-100" @click="handleCancel">
                  {{ t('buttons.cancel') }}
                </button>
              </div>
              <div class="col-6">
                <button type="submit" class="btn btn-success w-100" :disabled="isSubmitting || !selectedBadgeId && !errors.badgeId">
                  <div v-if="isSubmitting" class="spinner-border spinner-border-sm " role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  {{ t('buttons.save') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showModal" class="modal-backdrop fade show"></div>
  </teleport>
</template>